import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { LOCAL_STORAGE_DEMO_PRIVATE_KEY } from "../constants";
import { useClient } from "../../contexts/ClientContext";
import AuthCard from "./AuthCard";
import {
  DEPARTMENT_OPTIONS,
  getRegisterErrorKey,
  validateRegisterForm,
} from "./helpers";

function CreateMemberPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createMember } = useClient();

  const [values, setValues] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    privateKey: localStorage.getItem(LOCAL_STORAGE_DEMO_PRIVATE_KEY) || "",
    department: "retail",
  });
  const [errorKey, setErrorKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorKey("");

    const validationErrorKey = validateRegisterForm(values);
    if (validationErrorKey) {
      setErrorKey(validationErrorKey);
      return;
    }

    setIsSubmitting(true);
    const response = await createMember({
      email: values.email,
      password: values.password,
      privateKey: values.privateKey,
      department: values.department,
    });
    setIsSubmitting(false);

    if (!response.success) {
      setErrorKey(getRegisterErrorKey(response.error));
      return;
    }

    localStorage.setItem(LOCAL_STORAGE_DEMO_PRIVATE_KEY, values.privateKey);
    navigate("/", { replace: true });
  };

  return (
    <AuthCard
      title={t("auth.createMemberTitle")}
      subtitle={t("auth.createMemberDescription")}
    >
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
        <TextField
          required
          type="email"
          label={t("auth.emailLabel")}
          value={values.email}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, email: event.target.value }))
          }
        />
        <TextField
          required
          type={showPassword ? "text" : "password"}
          label={t("auth.passwordLabel")}
          helperText={t("auth.passwordMinLength")}
          value={values.password}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, password: event.target.value }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("auth.togglePasswordVisibility")}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          type={showConfirmPassword ? "text" : "password"}
          label={t("auth.confirmPasswordLabel")}
          value={values.confirmPassword}
          onChange={(event) =>
            setValues((prev) => ({
              ...prev,
              confirmPassword: event.target.value,
            }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("auth.togglePasswordVisibility")}
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          type={showPrivateKey ? "text" : "password"}
          label={t("auth.privateKeyLabel")}
          value={values.privateKey}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, privateKey: event.target.value }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("auth.togglePrivateKeyVisibility")}
                  onClick={() => setShowPrivateKey((prev) => !prev)}
                  edge="end"
                >
                  {showPrivateKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          select
          label={t("auth.departmentLabel")}
          value={values.department}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, department: event.target.value }))
          }
        >
          {DEPARTMENT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>

        {errorKey ? <Typography color="error">{t(errorKey)}</Typography> : null}

        <Button type="submit" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? t("common.loading") : t("auth.createMemberButton")}
        </Button>
        <Button component={RouterLink} to="/sign-in">
          {t("auth.goToSignIn")}
        </Button>
      </Stack>
    </AuthCard>
  );
}

export default CreateMemberPage;
